import React from 'react';
import LgmButton from './LgmButton';

export type ProductSearchMode = 'internal' | 'fda_sources';

interface FdaSourceSearchToggleProps {
  mode: ProductSearchMode;
  onChange: (mode: ProductSearchMode) => void;
}

/**
 * Internal / FDA Sources search-mode toggle for the Products page basic
 * search (docs/features/fda-source-product-search.md, Task FSS-006).
 *
 * Rendered via `UnifiedSearchBar`'s `basicSearchModeToggle` slot, which
 * already only shows it during basic search (Requirement 1's
 * basic-search-only scope is enforced by the slot's own placement, not by
 * this component).
 *
 * Session-only state — the caller (ProductsRowGroupingDataGrid) owns
 * `mode` as plain component state, never persisted to localStorage
 * (resolved Open Question 1: a page refresh always resets to Internal).
 */
const FdaSourceSearchToggle = ({
  mode,
  onChange,
}: FdaSourceSearchToggleProps) => {
  return (
    <div
      role="group"
      aria-label="Search mode"
      style={styles.container}
      data-testid="fda-source-search-toggle">
      <LgmButton
        type="button"
        variant={mode === 'internal' ? 'contained' : 'outlined'}
        color="primary"
        aria-pressed={mode === 'internal'}
        style={styles.leftButton}
        onClick={() => onChange('internal')}>
        Internal
      </LgmButton>
      <LgmButton
        type="button"
        variant={mode === 'fda_sources' ? 'contained' : 'outlined'}
        color="primary"
        aria-pressed={mode === 'fda_sources'}
        style={styles.rightButton}
        onClick={() => onChange('fda_sources')}>
        FDA Sources
      </LgmButton>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    gap: '0.25rem',
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
  },
  leftButton: {
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    padding: '0.375rem 0.75rem',
  },
  rightButton: {
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    padding: '0.375rem 0.75rem',
  },
};

export default FdaSourceSearchToggle;
